import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { useAppContext } from "components/Context/AppContext";
import CardTools, { ExpandModal } from "components/CardTools";
import { downloadCsv } from "utils/downloadCsv";
import { fmtDec } from "utils/format";
import "styles/dashboard.css";
import "./stress.css";

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatUSD(v) {
  const abs = Math.abs(v), sign = v < 0 ? "−" : "+";
  return abs >= 1
    ? `${sign}$${abs.toFixed(2)}M`
    : `${sign}$${(abs * 1000).toFixed(0)}K`;
}

function severityColor(absPct) {
  if (absPct < 5)  return "#22d47e";  // green
  if (absPct < 15) return "#C9A84C";  // amber
  return "#e05252";                    // red
}

function barGradient(color, isNeg) {
  const dir = isNeg ? "to left" : "to right";
  return `linear-gradient(${dir}, ${color}88, ${color}18)`;
}

// ── Build flat row list with section headers interleaved ──────────────────────
function buildRows(scenarios) {
  const rows = [];
  let lastType = null;
  for (const s of scenarios) {
    if (s.type !== lastType) {
      rows.push({ isHeader: true, type: s.type });
      lastType = s.type;
    }
    rows.push({ isHeader: false, scenario: s });
  }
  return rows;
}

// ── Footer stats ──────────────────────────────────────────────────────────────
function footerStats(scenarios) {
  const worst  = scenarios.reduce((a, b) => a.impactUSD < b.impactUSD ? a : b);
  const sorted = [...scenarios].sort((a, b) => a.impactUSD - b.impactUSD);
  const median = sorted[Math.floor(sorted.length / 2)];
  return { worst, median };
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function StressScenariosPage() {
  const { selectedAccount } = useAppContext();
  const [theme, setTheme]   = useState(() => localStorage.getItem("trg_theme") || "dark");
  const [scenarios, setScenarios] = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [expandOpen, setExpandOpen] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const aid   = selectedAccount?.account_id;
    if (!token || !aid) return;

    setLoading(true);
    fetch(`${DASHBOARD_API}/stress/scenarios?token=${token}&account_id=${aid}`)
      .then(r => r.json())
      .then(data => {
        setScenarios(Array.isArray(data) && data.length ? data : null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [selectedAccount]);

  const MAX_ABS = scenarios ? Math.max(...scenarios.map(s => Math.abs(s.impactPct))) : 1;
  const SCALE   = 48 / MAX_ABS;
  const rows    = scenarios ? buildRows(scenarios) : [];
  const stats   = scenarios ? footerStats(scenarios) : null;

  const handleCsv = () => downloadCsv(
    "stress_scenarios",
    [
      { label: "Scenario",     accessor: r => r.name },
      { label: "Period",       accessor: r => r.period },
      { label: "Impact (USD)", accessor: r => r.impactUSD, fmt: v => formatUSD(v) },
      { label: "Impact (%)",   accessor: r => r.impactPct, fmt: v => (v < 0 ? "" : "+") + fmtDec(v) + "%" },
    ],
    scenarios || []
  );

  const scenarioTable = (
    <table className="ss-table">
      <thead>
        <tr>
          <th className="ss-td-scenario">Scenario</th>
          <th className="ss-td-period">Period</th>
          <th className="ss-td-impact-usd ss-right">Impact (USD)</th>
          <th className="ss-td-bar">Impact (%)</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => {
          if (row.isHeader) {
            return (
              <tr key={`hdr-${row.type}`} className="ss-section-header">
                <td colSpan={4}>
                  <span className="ss-section-header-label">{row.type} Scenarios</span>
                </td>
              </tr>
            );
          }

          const s      = row.scenario;
          const neg    = s.impactPct < 0;
          const absPct = Math.abs(s.impactPct);
          const barW   = (absPct * SCALE).toFixed(2);
          const color  = severityColor(absPct);
          const pctStr = (neg ? "" : "+") + fmtDec(s.impactPct) + "%";

          return (
            <tr key={s.name}>
              <td className="ss-td-scenario">
                <div className="ss-scenario-name">{s.name}</div>
              </td>
              <td className="ss-td-period">
                <span className="ss-period-label">{s.period}</span>
              </td>
              <td className="ss-td-impact-usd ss-right">
                <span className="ss-impact-usd" style={{ color: "var(--dash-text)" }}>{formatUSD(s.impactUSD)}</span>
              </td>
              <td className="ss-td-bar">
                <div className="ss-bar-outer">
                  <div className="ss-bar-zero" />
                  <div
                    className={`ss-bar-fill ${neg ? "neg" : "pos"}`}
                    style={{
                      width: `${barW}%`,
                      background: barGradient(color, neg),
                      boxShadow: `0 0 8px ${color}33`,
                    }}
                  />
                  <span className={`ss-pct-val${neg ? " neg" : " pos"}`} style={{ color: "var(--dash-text)" }}>
                    {pctStr}
                  </span>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <Helmet>
        <title>Stress Scenarios – Tail Risk Global</title>
      </Helmet>

      <div className={`trg-shell${theme === "light" ? " theme-light" : ""}`}>
        <DashboardTopbar />
        <DashboardMenuBar onThemeChange={setTheme} />

        <main className="ss-body">

          {/* ── Page header ── */}
          <div className="ss-page-hdr">
            <div>
              <div className="ss-page-title">Stress Test &amp; Scenario Analysis</div>
              <div className="ss-page-sub">
                Portfolio impact under historical and hypothetical stress scenarios
              </div>
            </div>
          </div>

          {loading && <div className="trg-loading">Loading…</div>}

          {/* ── Scenario table ── */}
          <div className="trg-card ss-table-card trg-tool-host">
            {scenarios && <CardTools onCsv={handleCsv} onExpand={() => setExpandOpen(true)} />}
            {!loading && !scenarios ? (
              <div className="trg-no-data">No data</div>
            ) : scenarios && (
              <>
                {scenarioTable}

                {/* ── Footer ── */}
                {stats && (
                  <div className="ss-footer">
                    <div className="ss-footer-stat">
                      <div className="ss-footer-stat-label">Worst Scenario</div>
                      <div className="ss-footer-stat-value" style={{ color: severityColor(Math.abs(stats.worst.impactPct)) }}>
                        {formatUSD(stats.worst.impactUSD)}
                      </div>
                      <div className="ss-footer-stat-sub">{stats.worst.name}</div>
                    </div>

                    <div className="ss-footer-divider" />

                    <div className="ss-footer-stat">
                      <div className="ss-footer-stat-label">Median Impact</div>
                      <div className="ss-footer-stat-value" style={{ color: severityColor(Math.abs(stats.median.impactPct)) }}>
                        {formatUSD(stats.median.impactUSD)}
                      </div>
                      <div className="ss-footer-stat-sub">{stats.median.name}</div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <ExpandModal open={expandOpen} title="Stress Scenarios" onClose={() => setExpandOpen(false)} onCsv={handleCsv}>
            {scenarioTable}
          </ExpandModal>

        </main>
      </div>
    </>
  );
}
